import React, { useContext, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Form, Card, PrimaryButton, TextField, TextFieldMultiLine, Radio, Dropdown } from '@hz-design-system/web-ui';

import EnvironmentContext from '../../../contexts/EnvironmentContext';

import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { useRecaptcha, useRecaptchaHandler } from '../../../hooks/useRecaptcha';

import useToastNotification from '../../../hooks/useToastNotification';
import type { TToastNotificationProps } from '../../../components/Toast/ToastNotification';
import { ToastContainer } from 'react-toastify';

import Classes from './PrivacyPageComponent.module.scss';
import submitPrivacyRequest from '../repositories/submitPrivacyRequest';

const PrivacyPageComponent = () => {
  const { xsrfToken, recaptchaSiteKey, recaptchaScriptUrl, tenantContext } = useContext(EnvironmentContext);
  const { toast } = useToastNotification<TToastNotificationProps>();
  const { t } = useI18nContext();
  useRecaptcha(recaptchaScriptUrl, recaptchaSiteKey);

  // Determine default language preference based on tenant context
  const getDefaultLanguagePreference = () => {
    const tenantMapping = `${tenantContext?.tenant}_${tenantContext?.locale}`;
    if (tenantMapping.toLowerCase() === 'twh_fr-be') {
      return 'French';
    }
    return 'Dutch'; // Default fallback
  };

  const dataSubjectRelationOptions = [
    { label: t('pages.privacy.form.data_subject_relation.self'), value: 'Dit ben ik zelf' },
    {
      label: t('pages.privacy.form.data_subject_relation.legal_representative'),
      value: 'Juridische vertegenwoordiger Gebruiker',
    },
    { label: t('pages.privacy.form.data_subject_relation.family'), value: 'Familie van de gebruiker' },
    { label: t('pages.privacy.form.data_subject_relation.other'), value: 'Anders' },
  ];

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<any>({
    mode: 'onChange',
    defaultValues: {
      dataSubjectRelation: 'Dit ben ik zelf',
      languagePreference: getDefaultLanguagePreference(),
    },
  });

  // Use a ref to store the current form data for the recaptcha handler
  const formDataRef = useRef<any>(null);

  const handleSubmitWithRecaptcha = useRecaptchaHandler({
    recaptchaSiteKey: recaptchaSiteKey || '',
    onSuccess: async (token: string) => {
      if (!formDataRef.current) return;

      const data = formDataRef.current;
      await submitPrivacyRequest({
        requestType: data.requestType,
        firstName: data.firstName,
        lastName: data.lastName,
        contactEmail: data.contactEmail,
        contactPhone: data.contactPhone,
        languagePreference: data.languagePreference,
        dataSubjectRelation: data.dataSubjectRelation,
        dataSubjectEmail: data.dataSubjectEmail,
        description: data.description,
        recaptchaToken: token,
        xsrfToken,
      });
      void toast({
        type: 'success',
        description: t('pages.privacy.form.success.description'),
      });
      // Reset the form after successful submission
      reset({
        dataSubjectRelation: 'Dit ben ik zelf',
        languagePreference: getDefaultLanguagePreference(),
      });
    },
    onError: () => {
      void toast({
        type: 'error',
        description: t('pages.deleteAccount.confirm.failure.description'),
      });
    },
  });

  const onSubmit = (data: any) => {
    // Store form data in ref and trigger recaptcha
    formDataRef.current = data;
    const syntheticEvent = {
      preventDefault: () => {},
    } as React.FormEvent<HTMLFormElement>;
    handleSubmitWithRecaptcha(syntheticEvent);
  };

  return (
    <div className={Classes.root}>
      <Card padding="m" style={{ textAlign: 'left' }}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <h1 className={Classes.header}>{t('pages.privacy.page_header')}</h1>

          <fieldset className={Classes.radioGroup}>
            <legend className={Classes.legend}>{t('pages.privacy.form.request_type.label')}</legend>
            <Controller
              name="requestType"
              control={control}
              rules={{ required: t('pages.privacy.form.request_type.required') }}
              render={({ field }) => (
                <>
                  <Radio
                    labelText={t('pages.privacy.form.request_type.sar')}
                    id="sar_request"
                    value="SAR"
                    isChecked={field.value === 'SAR'}
                    onChange={() => field.onChange('SAR')}
                  />
                  <Radio
                    labelText={t('pages.privacy.form.request_type.ddr')}
                    id="ddr_request"
                    value="DDR"
                    isChecked={field.value === 'DDR'}
                    onChange={() => field.onChange('DDR')}
                  />
                </>
              )}
            />
          </fieldset>
          {errors.requestType && (
            <div style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
              {errors.requestType?.message as string}
            </div>
          )}

          <Controller
            name="firstName"
            control={control}
            rules={{ required: t('pages.privacy.form.first_name.required') }}
            render={({ field }) => (
              <TextField
                labelText={t('pages.privacy.form.first_name.label')}
                id="firstName"
                value={field.value || ''}
                onChange={(e) => field.onChange((e.target as HTMLInputElement).value)}
                errorText={errors.firstName?.message as string}
              />
            )}
          />

          <Controller
            name="lastName"
            control={control}
            rules={{ required: t('pages.privacy.form.last_name.required') }}
            render={({ field }) => (
              <TextField
                labelText={t('pages.privacy.form.last_name.label')}
                id="lastName"
                value={field.value || ''}
                onChange={(e) => field.onChange((e.target as HTMLInputElement).value)}
                errorText={errors.lastName?.message as string}
              />
            )}
          />

          <Controller
            name="contactEmail"
            control={control}
            rules={{
              required: t('pages.privacy.form.contact_email.required'),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t('pages.privacy.form.contact_email.invalid'),
              },
            }}
            render={({ field }) => (
              <TextField
                labelText={t('pages.privacy.form.contact_email.label')}
                id="contactEmail"
                type="email"
                value={field.value || ''}
                onChange={(e) => field.onChange((e.target as HTMLInputElement).value)}
                errorText={errors.contactEmail?.message as string}
              />
            )}
          />

          <Controller
            name="contactPhone"
            control={control}
            rules={{
              pattern: {
                value: /^[\d+]+$/,
                message: t('pages.privacy.form.contact_phone.invalid'),
              },
            }}
            render={({ field }) => (
              <TextField
                labelText={t('pages.privacy.form.contact_phone.label')}
                id="contactPhone"
                type="tel"
                value={field.value || ''}
                onChange={(e) => field.onChange((e.target as HTMLInputElement).value)}
                errorText={errors.contactPhone?.message as string}
              />
            )}
          />

          <fieldset className={Classes.radioGroup}>
            <legend className={Classes.legend}>{t('pages.privacy.form.language_preference.label')}</legend>
            <Controller
              name="languagePreference"
              control={control}
              rules={{ required: t('pages.privacy.form.language_preference.required') }}
              render={({ field }) => (
                <>
                  <Radio
                    labelText={t('pages.privacy.form.language_preference.dutch')}
                    id="lang_dutch"
                    value="Dutch"
                    isChecked={field.value === 'Dutch'}
                    onChange={() => field.onChange('Dutch')}
                  />
                  <Radio
                    labelText={t('pages.privacy.form.language_preference.french')}
                    id="lang_french"
                    value="French"
                    isChecked={field.value === 'French'}
                    onChange={() => field.onChange('French')}
                  />
                  <Radio
                    labelText={t('pages.privacy.form.language_preference.english')}
                    id="lang_english"
                    value="English"
                    isChecked={field.value === 'English'}
                    onChange={() => field.onChange('English')}
                  />
                </>
              )}
            />
          </fieldset>
          {errors.languagePreference && (
            <div style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
              {errors.languagePreference?.message as string}
            </div>
          )}

          <Controller
            name="dataSubjectRelation"
            control={control}
            rules={{ required: t('pages.privacy.form.data_subject_relation.required') }}
            render={({ field }) => (
              <Dropdown
                labelText={t('pages.privacy.form.data_subject_relation.label')}
                id="dataSubjectRelation"
                options={dataSubjectRelationOptions}
                value={field.value}
                onChange={(e) => field.onChange((e.target as HTMLSelectElement).value)}
                placeHolderText={t('pages.privacy.form.data_subject_relation.self')}
              />
            )}
          />
          {errors.dataSubjectRelation && (
            <div style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
              {errors.dataSubjectRelation?.message as string}
            </div>
          )}

          <Controller
            name="dataSubjectEmail"
            control={control}
            rules={{
              required: t('pages.privacy.form.data_subject_email.required'),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t('pages.privacy.form.data_subject_email.invalid'),
              },
            }}
            render={({ field }) => (
              <TextField
                labelText={t('pages.privacy.form.data_subject_email.label')}
                id="dataSubjectEmail"
                type="email"
                value={field.value || ''}
                onChange={(e) => field.onChange((e.target as HTMLInputElement).value)}
                errorText={errors.dataSubjectEmail?.message as string}
                helperText={t('pages.privacy.form.data_subject_email.tip')}
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            rules={{ required: t('pages.privacy.form.description.required') }}
            render={({ field }) => (
              <TextFieldMultiLine
                labelText={t('pages.privacy.form.description.label')}
                id="description"
                placeholder={t('pages.privacy.form.description.placeholder')}
                value={field.value || ''}
                onChange={(e) => field.onChange((e.target as HTMLInputElement).value)}
                errorText={errors.description?.message as string}
              />
            )}
          />

          <PrimaryButton type="submit">{t('pages.privacy.form.submit')}</PrimaryButton>
        </Form>
      </Card>
      <ToastContainer />
    </div>
  );
};

export default PrivacyPageComponent;
